import LogManager from "./LogManager";
import { AVAILABILITY_TIMEOUT_ERROR } from "../../constants";

/**
 * Determines whether the given alias is present in the semicolon-separated email list string.
 * (e.g., "user1@amazon.com; user2@amazon.com").
 * 
 * @param {string} emailListStr - Semicolon-separated list of email addresses.
 * @param {string} alias - The alias to search for in the local part of the email.
 * @returns {boolean} True if the alias is found in any email address; false otherwise.
 */
function isAliasExistInEmailList(emailListStr, alias) {
  if (!emailListStr || typeof emailListStr !== "string") {
    LogManager.warn("Expected emailListStr to be a non-empty string");
    return false;
  }

  if (!alias || typeof alias !== "string") {
    LogManager.warn("Alias is missing or invalid");
    return false;
  }

  const lowerCaseAlias = alias.toLowerCase();

  const emails = emailListStr
    .split(";")
    .map(email => email.trim().toLowerCase());

  return emails.some(email => email.includes(lowerCaseAlias));
}

/**
 * Calls the backend API to check if AIMS is available for the meeting.
 * @param {string} meetingUUID - The unique ID of the meeting.
 * @param {string} meetingId - The meeting ID shown in Zoom.
 * @param {string} meetingTopic - The topic/title of the meeting.
 * @returns {Promise<object|null>} AIMS availability result, or null if an error occurred.
 */
export const fetchAimsAvailabilityApi = async (
  meetingUUID,
  meetingId,
  meetingTopic,
) => {
  try {
    const data = {
      meetingUniqueId: meetingUUID,
      platform: "zoom",
      role: "host",
      meetingTopic,
      zoomMeetingId: meetingId,
    };
    LogManager.info(
      "Calling /is-aims-available. Request body is: " + JSON.stringify(data)
    );

    const response = await fetch("/is-aims-available", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      if (response.status === 504) {
        throw new Error(AVAILABILITY_TIMEOUT_ERROR);
      }
      throw new Error(
        `/is-aims-available response failed with status ${response.status}`
      );
    }

    const responseJson = await response.json();
    LogManager.info("Parsed /is-aims-available response JSON:", responseJson);

    if (!responseJson?.Payload) {
      throw new Error("Missing Payload in /is-aims-available response");
    }

    const payloadString = JSON.parse(responseJson.Payload);

    //sample: "{\"result\":\"AimsAvailable\",\"message\":null}"
    LogManager.info("Parsed payload string:", payloadString);

    const payload = JSON.parse(payloadString);
    
    // The user alias from backend
    const alias = payload.alias;
    
    // Get role directly from backend response
    const role = payload.role || "attendee"; // default to attendee if role is missing

    return payload;
  } catch (error) {
    LogManager.error("Error calling /is-aims-available:", error);
    if (error.message === AVAILABILITY_TIMEOUT_ERROR) {
      throw error;
    }
    return null;
  }
};
